import React, { useState, useEffect, useCallback, useRef } from "react";
import { staffQueue } from "../../api";

/**
 * Minimal-CHROME (not minimal-feature) queue widget for embedding in a
 * third-party host's docked sidebar (Elyon HMS's QueueDockPanel) — no
 * StaffLayout/DashboardLayout wrapper (no sidebar nav, no theme toggle,
 * no sign-out), but covers every real operational action the full
 * StaffQueuePage does: Call Next, Recall, Transfer, Skip, Hold, Mark
 * Emergency, Take a Break, Change Counter, Manual Ticket, and messaging.
 * Explicit design call: staff shouldn't have to leave whatever HMS page
 * they're on for anything OPERATIONAL — only for REPORTS (My Performance
 * etc.), which stay full-page-only since those aren't time-critical.
 */
export default function CompactQueuePage() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [busy, setBusy]         = useState(false);
  const [thread, setThread]     = useState(null);
  const [message, setMessage]   = useState("");
  const [sending, setSending]   = useState(false);
  const [outcomeId, setOutcomeId] = useState("");
  const [onBreak, setOnBreak]   = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualName, setManualName]   = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const pollRef = useRef(null);
  const counterResolvedForStation = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await staffQueue.myQueue();
      setData(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Could not reach the queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const loadThread = useCallback(async (ticketId) => {
    try {
      const { data } = await staffQueue.conversation(ticketId);
      setThread(data.thread);
    } catch {
      setThread([]);
    }
  }, []);

  // Same pattern as the full StaffQueuePage: a station with exactly one
  // active counter has no real choice to make, so pick it silently
  // rather than making staff open a dropdown for a foregone conclusion —
  // this is also what actually fixes call-next attaching no counter,
  // since callNext() below reads data.selected_counter.
  useEffect(() => {
    if (!data?.station) return;
    const stationId = data.station.id;
    if (counterResolvedForStation.current === stationId) return;
    counterResolvedForStation.current = stationId;

    const counters = data.station.counters || [];
    if (!data.selected_counter && counters.length === 1) {
      staffQueue.selectCounter(counters[0].id).then(load).catch(() => {});
    }
  }, [data?.station, data?.selected_counter, load]);

  useEffect(() => {
    if (data?.serving?.id) {
      loadThread(data.serving.id);
    } else {
      setThread(null);
    }
  }, [data?.serving?.id]);

  const runAction = async (fn, errMsg) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || errMsg);
    } finally {
      setBusy(false);
    }
  };

  const callNext = () => {
    // BUG FIX: call-next requires counter_id explicitly in the request
    // body -- it does NOT fall back to the staff's saved counter server-
    // side. Sending {} silently called the ticket into "serving" with no
    // counter attached, which the Display Board's per-counter rendering
    // (and its "Now Serving" hero, keyed off a counter) then couldn't
    // show at all, even though the raw waiting/serving counts were
    // correct -- confirmed live: a ticket really was status=serving with
    // current_counter=None.
    const counterId = data?.selected_counter?.id;
    return runAction(() => staffQueue.callNext(counterId ? { counter_id: counterId } : {}), "Could not call next.");
  };
  const recall           = () => data?.serving?.id && runAction(() => staffQueue.recallTicket(data.serving.id), "Could not recall.");
  const skip              = () => data?.serving?.id && runAction(() => staffQueue.skip(data.serving.id), "Could not skip.");
  const hold               = () => data?.serving?.id && runAction(() => staffQueue.hold(data.serving.id), "Could not hold.");
  const markEmergency   = () => data?.serving?.id && runAction(() => staffQueue.escalatePriority(data.serving.id, "emergency"), "Could not mark emergency.");
  const changeCounter    = (counterId) => runAction(() => staffQueue.selectCounter(counterId), "Could not change counter.");

  const toggleBreak = async () => {
    setBusy(true);
    try {
      if (onBreak) { await staffQueue.imBack(); setOnBreak(false); }
      else         { await staffQueue.goAway(); setOnBreak(true); }
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not update break status.");
    } finally {
      setBusy(false);
    }
  };

  const transfer = async () => {
    if (!data?.serving?.id || !outcomeId) return;
    setBusy(true);
    try {
      await staffQueue.transfer(data.serving.id, { outcome_id: outcomeId });
      setOutcomeId("");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not transfer.");
    } finally {
      setBusy(false);
    }
  };

  const issueManual = async () => {
    if (!manualPhone.trim()) return;
    setBusy(true);
    try {
      await staffQueue.issueManual({
        patient_name: manualName.trim() || "Walk-in Patient",
        patient_phone: manualPhone.trim(),
      });
      setManualName(""); setManualPhone(""); setShowManual(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Could not issue ticket.");
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !data?.serving?.id) return;
    setSending(true);
    try {
      await staffQueue.sendMessage(data.serving.id, { text: message.trim() });
      setMessage("");
      await loadThread(data.serving.id);
    } catch (err) {
      setError(err.response?.data?.error || "Could not send message.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div style={S.root}>
        <span style={S.dim}>Loading…</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={S.root}>
        <span style={S.dim}>{error}</span>
        <button style={S.btnGhost} onClick={load}>Try again</button>
      </div>
    );
  }

  const serving = data?.serving;
  const waitingCount = data?.waiting_count ?? 0;
  const counters = data?.station?.counters || [];
  const selectedCounterId = data?.selected_counter?.id || "";

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div style={S.stationName}>{data?.station?.name || "Station"}</div>
        <div style={S.waitingPill}>{waitingCount} waiting</div>
      </div>

      {/* Top utility row — counter + break, always available regardless of serving state */}
      <div style={S.utilityRow}>
        {counters.length > 0 && (
          <select
            style={S.utilitySelect}
            value={selectedCounterId}
            onChange={(e) => e.target.value && changeCounter(e.target.value)}
            disabled={busy}
          >
            <option value="">Counter…</option>
            {counters.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        <button style={S.btnGhostSmall} onClick={toggleBreak} disabled={busy}>
          {onBreak ? "▶ I'm Back" : "⏸ Take a Break"}
        </button>
        <button style={S.btnGhostSmall} onClick={() => setShowManual((v) => !v)} disabled={busy}>
          + Manual Ticket
        </button>
      </div>

      {showManual && (
        <div style={S.manualForm}>
          <input
            style={S.convoInput}
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
            placeholder="Patient name"
          />
          <input
            style={S.convoInput}
            value={manualPhone}
            onChange={(e) => setManualPhone(e.target.value)}
            placeholder="Phone number"
          />
          <button style={S.btnPrimarySmall} onClick={issueManual} disabled={busy || !manualPhone.trim()}>
            Issue
          </button>
        </div>
      )}

      {error && <div style={S.errorBanner}>{error}</div>}

      <div style={S.servingCard}>
        {serving ? (
          <>
            <div style={S.servingLabel}>NOW SERVING</div>
            <div style={S.servingNumber}>{serving.ticket_number || serving.number}</div>
            {serving.patient_name && <div style={S.servingName}>{serving.patient_name}</div>}

            <div style={S.actionGrid}>
              <button style={S.btnGhost} onClick={recall} disabled={busy}>↺ Recall</button>
              <button style={S.btnGhost} onClick={skip} disabled={busy}>⏭ Skip</button>
              <button style={S.btnGhost} onClick={hold} disabled={busy}>⏸ Hold</button>
              <button style={S.btnDanger} onClick={markEmergency} disabled={busy || serving.priority === "emergency"}>
                🚨 Emergency
              </button>
            </div>

            {Array.isArray(data?.outcomes) && data.outcomes.length > 0 && (
              <div style={S.transferRow}>
                <select
                  style={S.transferSelect}
                  value={outcomeId}
                  onChange={(e) => setOutcomeId(e.target.value)}
                >
                  <option value="">Transfer to…</option>
                  {data.outcomes.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.is_exit ? `${o.label_en} (exit)` : `${o.label_en}${o.next_station__name ? ` → ${o.next_station__name}` : ""}`}
                    </option>
                  ))}
                </select>
                <button style={S.btnGhost} onClick={transfer} disabled={busy || !outcomeId}>
                  Go
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={S.readyDot} />
            <div style={S.readyText}>Ready</div>
            <div style={S.dim}>{waitingCount > 0 ? `${waitingCount} waiting` : "Queue is empty"}</div>
          </>
        )}
      </div>

      <button
        style={{ ...S.btnPrimary, opacity: busy || serving || waitingCount === 0 ? 0.5 : 1 }}
        onClick={callNext}
        disabled={busy || !!serving || waitingCount === 0}
        title={serving ? "Transfer, skip or hold the current ticket first" : undefined}
      >
        {busy ? "…" : "Call Next"}
      </button>

      {serving && (
        <div style={S.convo}>
          <div style={S.convoLabel}>Conversation</div>
          <div style={S.convoThread}>
            {thread === null ? (
              <span style={S.dim}>Loading…</span>
            ) : thread.length === 0 ? (
              <span style={S.dim}>No messages yet.</span>
            ) : (
              thread.map((m) => (
                <div
                  key={m.id}
                  style={{
                    ...S.bubble,
                    ...(m.from === "staff" ? S.bubbleStaff : S.bubblePatient),
                  }}
                >
                  {m.text}
                </div>
              ))
            )}
          </div>
          <div style={S.convoInputRow}>
            <input
              style={S.convoInput}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Message patient…"
            />
            <button style={S.btnSend} onClick={sendMessage} disabled={sending || !message.trim()}>
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  root: {
    minHeight: "100vh",
    background: "#0B1530",
    color: "#F1F5F9",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  stationName: { fontSize: 16, fontWeight: 700 },
  waitingPill: {
    fontSize: 11,
    fontWeight: 700,
    background: "rgba(37,99,235,0.18)",
    color: "#60A5FA",
    padding: "4px 10px",
    borderRadius: 20,
  },
  utilityRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  utilitySelect: {
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "5px 8px", color: "#F1F5F9", fontSize: 11, outline: "none",
  },
  btnGhostSmall: {
    background: "rgba(255,255,255,0.06)", color: "#CBD5E1",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
  },
  manualForm: {
    display: "flex", flexDirection: "column", gap: 6,
    background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: 10,
  },
  btnPrimarySmall: {
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#fff", border: "none", borderRadius: 8,
    padding: "7px 0", fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
  errorBanner: {
    fontSize: 12,
    color: "#FCA5A5",
    background: "rgba(220,38,38,0.12)",
    border: "1px solid rgba(220,38,38,0.3)",
    borderRadius: 8,
    padding: "8px 10px",
  },
  servingCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: 20,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
  },
  servingLabel: { fontSize: 11, fontWeight: 700, color: "#60A5FA", letterSpacing: "0.05em" },
  servingNumber: { fontSize: 32, fontWeight: 800 },
  servingName: { fontSize: 13, color: "#94A3B8" },
  actionGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8, width: "100%",
  },
  transferRow: { display: "flex", gap: 6, marginTop: 8, width: "100%" },
  transferSelect: {
    flex: 1, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "6px 8px", color: "#F1F5F9", fontSize: 12, outline: "none",
  },
  readyDot: {
    width: 14, height: 14, borderRadius: "50%",
    background: "#34D399", marginBottom: 4,
  },
  readyText: { fontSize: 18, fontWeight: 700 },
  dim: { fontSize: 13, color: "#64748B" },
  btnPrimary: {
    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
    color: "#fff", border: "none", borderRadius: 10,
    padding: "12px 0", fontSize: 14, fontWeight: 700, cursor: "pointer",
  },
  btnGhost: {
    background: "rgba(255,255,255,0.06)", color: "#CBD5E1",
    border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
    padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
  },
  btnDanger: {
    background: "rgba(220,38,38,0.15)", color: "#FCA5A5",
    border: "1px solid rgba(220,38,38,0.35)", borderRadius: 8,
    padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
  },
  convo: {
    display: "flex", flexDirection: "column", gap: 8, flex: 1, minHeight: 0,
  },
  convoLabel: { fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.04em" },
  convoThread: {
    flex: 1, minHeight: 80, maxHeight: 220, overflowY: "auto",
    display: "flex", flexDirection: "column", gap: 6,
    background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: 10,
  },
  bubble: {
    fontSize: 13, padding: "7px 10px", borderRadius: 10, maxWidth: "85%",
  },
  bubbleStaff: { alignSelf: "flex-end", background: "#2563EB", color: "#fff" },
  bubblePatient: { alignSelf: "flex-start", background: "rgba(255,255,255,0.08)", color: "#E2E8F0" },
  convoInputRow: { display: "flex", gap: 8 },
  convoInput: {
    flex: 1, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "8px 10px", color: "#F1F5F9", fontSize: 13, outline: "none",
  },
  btnSend: {
    background: "#2563EB", color: "#fff", border: "none", borderRadius: 8,
    padding: "0 14px", fontSize: 14, cursor: "pointer",
  },
};
